/**
 * Widget de suivi de commande LMC.
 * Appelle l'API publique LMC+ et rend le resultat dans la section.
 *
 * Contrat API : voir https://github.com/grouperm/lmc-plus/blob/master/docs/api-public.md
 * POST {API_URL} { reference } -> { ok, data | error }
 *
 * A11y :
 *  - aria-live polite sur la zone resultat (annonce lecteur d'ecran)
 *  - field errors lies via aria-describedby
 *  - focus management sur erreur globale et succes
 *
 * Resilience :
 *  - AbortController sur soumission successive (anti double-click)
 *  - Validation locale legere AVANT envoi (epargne le quota rate-limit)
 *  - Tous les codes d'erreur API mappes a un message FR-FR generique
 *    (ne revele pas la cause exacte sur rate_limited -> anti-enum)
 */
(function () {
  'use strict';

  const API_URL =
    (window.LMC_TRACKING_API_URL ||
      'https://lmc-plus.vercel.app/api/public/v1/orders/lookup');

  /** Endpoint de satisfaction : meme base que le lookup (override compris). */
  const FEEDBACK_URL = API_URL.replace(/\/lookup$/, '/feedback');

  const REFERENCE_RE = /^\d{6,12}$/;

  /** Libelles UI centralises (FR). Modifier ici pour i18n future. */
  const T = {
    errors: {
      invalid_input_generic: 'Veuillez vérifier les informations saisies.',
      invalid_json: 'Une erreur est survenue. Réessayez.',
      forbidden: 'Accès refusé. Rechargez la page depuis le site officiel.',
      not_found:
        'Nous n’avons pas retrouvé de commande avec ces informations. Vérifiez votre numéro de commande, ou contactez directement votre magasin — il a votre dossier complet.',
      payload_too_large: 'Données trop volumineuses. Réessayez.',
      rate_limited:
        'Trop de tentatives. Patientez quelques minutes avant de réessayer.',
      server_error:
        'Le service est momentanément indisponible. Réessayez dans quelques instants.',
      network: 'Connexion impossible. Vérifiez votre réseau et réessayez.',
    },
    fields: {
      reference_invalid: 'Numéro invalide (6 à 12 chiffres).',
    },
    steps: {
      confirmed: 'Confirmée',
      ordered: 'En commande',
      warehouse: 'Réceptionné',
      shipping: 'En livraison',
      delivered: 'Livrée',
    },
    state: {
      cancelled: 'Cette commande a été annulée.',
      unknown:
        'Statut non disponible pour le moment. Contactez votre magasin pour plus d’informations.',
    },
    kind: {
      order: 'Commande',
      quote: 'Devis',
    },
    dates: {
      confirmedAt: 'Signée le',
      estimatedDeliveryAt: 'Livraison estimée',
      deliveredAt: 'Livrée le',
      statusUpdatedAt: 'Maj statut',
    },
    products_title: 'Produits',
    store_label: 'Magasin référent',
    store_online: 'Site Web',
    phone_label: 'Téléphone',
    feedback: {
      question: 'Cette page a-t-elle répondu à votre question ?',
      yes: 'Oui',
      no: 'Non',
      reasonsTitle: 'Qu’est-ce qui n’allait pas ?',
      commentPlaceholder: 'Précisez si vous le souhaitez (facultatif)',
      send: 'Envoyer',
      thanks: 'Merci pour votre retour !',
      thanksNeg:
        'Merci, c’est noté. Pour une réponse personnalisée, votre magasin reste votre meilleur contact.',
      // Raisons orientees PAGE/INFO uniquement (jamais le delai/la livraison).
      // Les valeurs DOIVENT correspondre a l'allow-list serveur (FEEDBACK_REASONS).
      reasons: [
        { value: 'info-incomplete', label: 'Il manque une information' },
        { value: 'info-pas-claire', label: 'Une information n’est pas claire' },
        { value: 'info-erronee', label: 'Une information semble fausse' },
        { value: 'autre', label: 'Autre' },
      ],
    },
  };

  /** Ordre canonique des etapes pour la timeline. */
  const STEPS = ['confirmed', 'ordered', 'warehouse', 'shipping', 'delivered'];

  document.addEventListener('DOMContentLoaded', init);

  function init() {
    const forms = document.querySelectorAll('[data-order-tracking-form]');
    forms.forEach(initForm);
  }

  /** Initialise une instance du widget (plusieurs peuvent coexister). */
  function initForm(form) {
    const refs = collectRefs(form);
    let inflight = null;

    form.addEventListener('submit', async function (event) {
      event.preventDefault();
      clearAll(refs);

      const payload = readPayload(form);
      const localErrors = validateLocal(payload);
      if (localErrors) {
        showFieldErrors(refs, localErrors);
        return;
      }

      if (inflight) inflight.abort();
      inflight = new AbortController();

      setLoading(refs, true);
      showSkeleton(refs);
      try {
        const res = await fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
          signal: typeof AbortSignal.timeout === 'function' ? AbortSignal.any([inflight.signal, AbortSignal.timeout(10000)]) : inflight.signal,
          credentials: 'omit',
        });
        const json = await res.json().catch(function () {
          return { ok: false, error: 'invalid_json' };
        });
        handleResponse(refs, json);
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        showGlobalError(refs, T.errors.network);
      } finally {
        setLoading(refs, false);
        inflight = null;
      }
    });
  }

  /**
   * Collecte tous les noeuds utiles du widget.
   * On scope sur le conteneur .order-tracking (root) car [data-message] et
   * [data-result] sont des freres du form, pas des descendants (le form.
   * querySelector ne les voyait pas -> null -> crash sur clearAll).
   */
  function collectRefs(form) {
    const root = form.closest('.order-tracking') || form.parentElement;
    return {
      form: form,
      root: root,
      submit: form.querySelector('[data-submit]'),
      message: root.querySelector('[data-message]'),
      result: root.querySelector('[data-result]'),
      referral: root.querySelector('[data-referral]'),
      inputs: {
        reference: form.querySelector('[name="reference"]'),
      },
      errors: {
        reference: form.querySelector('[data-error-reference]'),
      },
    };
  }

  function readPayload(form) {
    const fd = new FormData(form);
    const reference = String(fd.get('reference') || '').trim().replace(/\s+/g, '');
    return { reference: reference };
  }

  /** Validation cote client miroir du serveur (anti-gachis rate-limit). */
  function validateLocal(p) {
    const errs = {};
    if (!REFERENCE_RE.test(p.reference)) errs.reference = T.fields.reference_invalid;
    return Object.keys(errs).length > 0 ? errs : null;
  }

  function setLoading(refs, isLoading) {
    refs.submit.disabled = isLoading;
    if (isLoading) {
      refs.submit.dataset.loading = 'true';
      if (!refs.submit.dataset.originalText) {
        refs.submit.dataset.originalText = refs.submit.textContent;
      }
      refs.submit.textContent = 'Recherche en cours';
    } else {
      delete refs.submit.dataset.loading;
      if (refs.submit.dataset.originalText) {
        refs.submit.textContent = refs.submit.dataset.originalText;
        delete refs.submit.dataset.originalText;
      }
    }
  }

  function clearAll(refs) {
    refs.message.hidden = true;
    refs.message.textContent = '';
    refs.message.classList.remove('order-tracking__message--info');
    if (refs.result) {
      refs.result.hidden = true;
      refs.result.innerHTML = '';
    }
    hideSkeleton(refs);
    if (refs.root) refs.root.removeAttribute('data-has-result');
    if (refs.referral) refs.referral.hidden = true;
    Object.keys(refs.errors).forEach(function (k) {
      const el = refs.errors[k];
      if (!el) return;
      el.hidden = true;
      el.textContent = '';
    });
    refs.inputs.reference.setAttribute('aria-invalid', 'false');
  }

  function showFieldErrors(refs, errs) {
    if (errs.reference && refs.errors.reference) {
      refs.errors.reference.textContent = errs.reference;
      refs.errors.reference.hidden = false;
      refs.inputs.reference.setAttribute('aria-invalid', 'true');
      refs.inputs.reference.focus();
    }
  }

  function showGlobalError(refs, text) {
    hideSkeleton(refs);
    refs.message.textContent = text;
    refs.message.hidden = false;
    refs.message.focus();
  }

  /** Affiche le skeleton loading (placeholders animes) pendant le fetch. */
  function showSkeleton(refs) {
    if (!refs.root) return;
    const skel = refs.root.querySelector('[data-skeleton]');
    if (!skel) return;
    skel.hidden = false;
    skel.removeAttribute('aria-hidden');
  }

  /** Masque le skeleton (au succes, erreur, abort, ou clearAll). */
  function hideSkeleton(refs) {
    if (!refs.root) return;
    const skel = refs.root.querySelector('[data-skeleton]');
    if (!skel) return;
    skel.hidden = true;
    skel.setAttribute('aria-hidden', 'true');
  }

  function handleResponse(refs, json) {
    hideSkeleton(refs);
    if (json.ok) return renderResult(refs, json.data);

    switch (json.error) {
      case 'invalid_input':
        if (json.fieldErrors) {
          showFieldErrors(refs, { reference: json.fieldErrors.reference });
        } else showGlobalError(refs, T.errors.invalid_input_generic);
        break;
      case 'invalid_json':
        showGlobalError(refs, T.errors.invalid_json);
        break;
      case 'forbidden':
        showGlobalError(refs, T.errors.forbidden);
        break;
      case 'not_found':
        showGlobalError(refs, T.errors.not_found);
        break;
      case 'payload_too_large':
        showGlobalError(refs, T.errors.payload_too_large);
        break;
      case 'rate_limited':
        showGlobalError(refs, T.errors.rate_limited);
        break;
      case 'server_error':
        showGlobalError(refs, T.errors.server_error);
        break;
      default:
        showGlobalError(refs, T.errors.server_error);
    }
  }

  /** Construit le HTML du resultat puis l'injecte. */
  function renderResult(refs, data) {
    if (!refs.result) return;
    hideSkeleton(refs);
    refs.result.innerHTML = buildResultHtml(data);
    refs.result.hidden = false;
    if (refs.referral) {
      refs.referral.hidden = !!(data.isOverdue || data.step === 'cancelled' || data.step === 'unknown');
    }
    if (refs.root) refs.root.setAttribute('data-has-result', 'true');
    refs.result.focus();
    mountFeedback(refs, data);
  }

  function buildResultHtml(data) {
    const isSpecialState = data.step === 'cancelled' || data.step === 'unknown';
    const parts = [];
    parts.push('<div class="order-tracking__result-header">');
    parts.push(
      '<span class="order-tracking__result-kind">' +
        escapeHtml(T.kind[data.kind] || T.kind.order) +
        '</span>',
    );
    parts.push(
      '<h2 class="order-tracking__result-ref">N° ' + escapeHtml(data.reference) + '</h2>',
    );
    parts.push('</div>');

    if (isSpecialState) {
      const banner = data.step === 'cancelled' ? T.state.cancelled : T.state.unknown;
      const cls =
        data.step === 'unknown'
          ? 'order-tracking__state-banner order-tracking__state-banner--unknown'
          : 'order-tracking__state-banner';
      parts.push('<div class="' + cls + '">' + escapeHtml(banner) + '</div>');
    } else {
      parts.push(buildTimeline(data.step));
    }

    const dates = buildDates(data);
    if (dates) parts.push(dates);

    if (data.products && data.products.length > 0) {
      parts.push(buildProducts(data.products));
    }

    if (data.store && data.store.name) {
      parts.push(buildStore(data.store));
    }

    return parts.join('');
  }

  function buildTimeline(currentStep) {
    var idx = STEPS.indexOf(currentStep);
    var fillPct = idx >= 0 ? (idx / (STEPS.length - 1)) * 100 : 0;
    var isDelivered = currentStep === 'delivered';

    var truckSvg =
      '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/>' +
      '<path d="M15 18h2"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/>' +
      '<circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>';

    var checkSvg =
      '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">' +
      '<polyline points="20 6 9 17 4 12"/></svg>';

    var homeSvg =
      '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"/>' +
      '<path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>';

    var html = ['<div class="ot-road" role="group" aria-label="Etapes de la commande">'];
    html.push('<div class="ot-road__bar">');
    html.push('<div class="ot-road__bar-bg"></div>');
    html.push('<div class="ot-road__bar-fill" style="width:' + fillPct + '%"></div>');

    STEPS.forEach(function (step, i) {
      var pct = (i / (STEPS.length - 1)) * 100;
      var isDone = i < idx;
      var isCurrent = i === idx;
      var cls = 'ot-road__dot';
      var inner = '';

      if (isDone) {
        cls += ' ot-road__dot--done';
        inner = checkSvg;
      } else if (isCurrent && isDelivered) {
        cls += ' ot-road__dot--arrived';
        inner = homeSvg;
      } else if (isCurrent) {
        cls += ' ot-road__dot--active';
        inner = truckSvg;
      }

      html.push('<div class="' + cls + '" style="left:' + pct + '%">' + inner + '</div>');
    });

    html.push('</div>');
    html.push('<div class="ot-road__labels">');
    STEPS.forEach(function (step, i) {
      var pct = (i / (STEPS.length - 1)) * 100;
      var cls = 'ot-road__label';
      if (i < idx) cls += ' ot-road__label--done';
      else if (i === idx) cls += ' ot-road__label--current';
      html.push('<span class="' + cls + '" style="left:' + pct + '%">' + escapeHtml(T.steps[step]) + '</span>');
    });
    html.push('</div></div>');
    return html.join('');
  }

  function buildDates(data) {
    var items = [];
    ['confirmedAt', 'estimatedDeliveryAt', 'deliveredAt'].forEach(function (k) {
      if (k === 'estimatedDeliveryAt' && data.step === 'shipping') return;
      if (data[k]) {
        var isOverdueValue = k === 'estimatedDeliveryAt' && data.isOverdue === true;
        var valueClass = isOverdueValue ? ' order-tracking__date-value--overdue' : '';
        items.push(
          '<div><span class="order-tracking__date-label">' +
            escapeHtml(T.dates[k]) +
            '</span>' +
            '<span class="order-tracking__date-value' + valueClass + '">' +
              escapeHtml(formatDate(data[k])) +
            '</span>' +
            '</div>',
        );
      }
    });
    if (items.length === 0) return '';
    var html = '<div class="order-tracking__dates">' + items.join('') + '</div>';
    // En livraison (RDV pris), on n'affiche ni la date estimee ni le rappel
    // qui s'y rapporte : le client est deja pris en charge par le transporteur.
    if (data.estimatedDeliveryAt && data.step !== 'shipping') {
      if (data.isOverdue === true && data.step === 'warehouse') {
        var storePhone = (data.store && data.store.phone) ? data.store.phone : null;
        var phoneLink = storePhone
          ? ' au <a href="tel:' + escapeHtml(storePhone.replace(/\s/g, '')) + '" style="color:#c5205b;text-decoration:underline;font-weight:500;">' + escapeHtml(storePhone) + '</a>'
          : '';
        html +=
          '<p class="order-tracking__date-overdue-msg order-tracking__date-overdue-msg--dispo">' +
          "Votre produit est disponible. Le service livraison vous contactera prochainement pour planifier votre livraison." +
          (storePhone
            ? " N'hésitez pas à contacter votre magasin" + phoneLink + " pour plus d'informations."
            : ' <a href="#ot-aide" style="color:#c5205b;text-decoration:underline;">Contacter mon magasin</a>') +
          '</p>';
      } else if (data.isOverdue === true) {
        var storePhone = (data.store && data.store.phone) ? data.store.phone : null;
        var phoneLink = storePhone
          ? ' au <a href="tel:' + escapeHtml(storePhone.replace(/\s/g, '')) + '" style="color:#c5205b;text-decoration:underline;font-weight:500;">' + escapeHtml(storePhone) + '</a>'
          : '';
        var contactAnchor = storePhone ? '' :
          ' <a href="#ot-aide" style="color:#c5205b;text-decoration:underline;">Contacter mon conseiller</a>';
        html +=
          '<p class="order-tracking__date-overdue-msg">' +
          "Votre commande prend un peu plus de temps que prévu. " +
          'Contactez votre conseiller' + phoneLink +
          " pour faire un point si nécessaire." + contactAnchor +
          '</p>';
      } else {
        html +=
          '<p class="order-tracking__date-disclaimer">' +
          '<svg class="order-tracking__date-disclaimer-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
          '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>' +
          '</svg>' +
          '<span>Date estimée à la commande, peut évoluer.</span>' +
          '</p>';
      }
    }
    return html;
  }

  function buildProducts(products) {
    const items = products
      .map(function (p) {
        return (
          '<li class="order-tracking__products-item">' +
          '<span>' +
          escapeHtml(p.name) +
          '</span>' +
          '<span class="order-tracking__products-qty">x' +
          (p.quantity || 1) +
          '</span>' +
          '</li>'
        );
      })
      .join('');
    return (
      '<div class="order-tracking__products">' +
      '<h3 class="order-tracking__products-title">' +
      escapeHtml(T.products_title) +
      '</h3>' +
      '<ul class="order-tracking__products-list">' +
      items +
      '</ul>' +
      '</div>'
    );
  }

  /** Vrai si le magasin est la vente en ligne (pas un point de vente physique). */
  function isOnlineStore(name) {
    return /vente\s+en\s+ligne/i.test(name || '');
  }

  function buildStore(store) {
    var online = isOnlineStore(store.name);
    var displayName = online ? T.store_online : store.name;

    var link = '';
    if (store.phone) {
      var telHref = 'tel:' + String(store.phone).replace(/[^+\d]/g, '');
      link =
        '<a class="order-tracking__store-phone" href="' +
        escapeHtml(telHref) +
        '">' +
        escapeHtml(store.phone) +
        '</a>';
    }

    // Magasin physique : tel collé au nom (rendu historique inchangé).
    // Site web : nom seul, puis ligne dédiée "Téléphone : ...".
    var nameLine =
      '<div class="order-tracking__store-name">' +
      escapeHtml(T.store_label) +
      ' : ' +
      escapeHtml(displayName) +
      (online ? '' : link) +
      '</div>';

    var phoneLine =
      online && link
        ? '<div class="order-tracking__store-phone-line">' +
          escapeHtml(T.phone_label) +
          ' : ' +
          link +
          '</div>'
        : '';

    return (
      '<div class="order-tracking__store">' + nameLine + phoneLine + '</div>'
    );
  }

  // === Module satisfaction (« cette page a-t-elle aidé ? ») ===============

  var THUMB_UP_SVG =
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M7 10v12"/><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z"/></svg>';

  var THUMB_DOWN_SVG =
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M17 14V2"/><path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z"/></svg>';

  /**
   * Envoi best-effort du retour. Ne bloque jamais l'UI : un retour perdu
   * (reseau, API down) n'a aucune consequence visible pour le client.
   * keepalive : la requete survit a une navigation immediate.
   */
  function sendFeedback(payload) {
    try {
      fetch(FEEDBACK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'omit',
        keepalive: true,
      }).catch(function () {});
    } catch (e) {
      /* no-op volontaire : la collecte d'avis ne doit jamais casser la page */
    }
  }

  /**
   * Derive l'etat de commande pour la segmentation (« satisfaction hors
   * retard »). Valeurs alignees sur l'allow-list serveur FEEDBACK_ORDER_STATES.
   */
  function deriveOrderState(data) {
    if (!data) return undefined;
    if (data.step === 'cancelled') return 'cancelled';
    if (data.step === 'delivered') return 'delivered';
    if (data.isOverdue === true) return 'late';
    if (data.step === 'unknown') return 'unknown';
    return 'on_time';
  }

  /** Injecte le module sous le resultat et cable les interactions. */
  function mountFeedback(refs, data) {
    if (!refs.result) return;
    var reference = data && data.reference ? String(data.reference) : '';
    var orderState = deriveOrderState(data);
    var wrap = document.createElement('div');
    wrap.className = 'order-tracking__feedback';
    wrap.setAttribute('data-feedback', '');
    wrap.innerHTML =
      '<p class="order-tracking__feedback-q">' +
      escapeHtml(T.feedback.question) +
      '</p><div class="order-tracking__feedback-actions">' +
      feedbackButton('data-fb-yes', THUMB_UP_SVG, T.feedback.yes) +
      feedbackButton('data-fb-no', THUMB_DOWN_SVG, T.feedback.no) +
      '</div>';
    refs.result.appendChild(wrap);

    var yes = wrap.querySelector('[data-fb-yes]');
    var no = wrap.querySelector('[data-fb-no]');
    if (yes) {
      yes.addEventListener('click', function () {
        sendFeedback({ helpful: true, reference: reference, orderState: orderState });
        feedbackThanks(wrap, true);
      });
    }
    if (no) {
      no.addEventListener('click', function () {
        feedbackReasons(wrap, reference, orderState);
      });
    }
  }

  function feedbackButton(attr, svg, label) {
    return (
      '<button type="button" class="order-tracking__feedback-btn" ' +
      attr +
      ' aria-label="' +
      escapeHtml(label) +
      '">' +
      svg +
      '<span>' +
      escapeHtml(label) +
      '</span></button>'
    );
  }

  /** Etape « pouce bas » : chips de raison + commentaire libre + envoi. */
  function feedbackReasons(wrap, reference, orderState) {
    var chips = T.feedback.reasons
      .map(function (r) {
        return (
          '<button type="button" class="order-tracking__feedback-chip" data-fb-reason="' +
          escapeHtml(r.value) +
          '">' +
          escapeHtml(r.label) +
          '</button>'
        );
      })
      .join('');
    wrap.innerHTML =
      '<p class="order-tracking__feedback-q">' +
      escapeHtml(T.feedback.reasonsTitle) +
      '</p><div class="order-tracking__feedback-chips">' +
      chips +
      '</div><textarea class="order-tracking__feedback-comment" data-fb-comment rows="2" maxlength="500" placeholder="' +
      escapeHtml(T.feedback.commentPlaceholder) +
      '"></textarea><button type="button" class="order-tracking__feedback-send" data-fb-send>' +
      escapeHtml(T.feedback.send) +
      '</button>';

    var selected = null;
    var chipEls = wrap.querySelectorAll('[data-fb-reason]');
    chipEls.forEach(function (el) {
      el.addEventListener('click', function () {
        if (selected === el) {
          selected = null;
          el.removeAttribute('data-selected');
          return;
        }
        chipEls.forEach(function (c) {
          c.removeAttribute('data-selected');
        });
        el.setAttribute('data-selected', 'true');
        selected = el;
      });
    });

    var send = wrap.querySelector('[data-fb-send]');
    if (send) {
      send.addEventListener('click', function () {
        var commentEl = wrap.querySelector('[data-fb-comment]');
        var comment = commentEl ? commentEl.value.trim() : '';
        var payload = { helpful: false, reference: reference, orderState: orderState };
        if (selected) payload.reason = selected.getAttribute('data-fb-reason');
        if (comment) payload.comment = comment;
        sendFeedback(payload);
        feedbackThanks(wrap, false);
      });
    }
  }

  /** Etat final : message de remerciement (positif ou negatif). */
  function feedbackThanks(wrap, positive) {
    wrap.innerHTML =
      '<p class="order-tracking__feedback-thanks">' +
      escapeHtml(positive ? T.feedback.thanks : T.feedback.thanksNeg) +
      '</p>';
  }

  /** Format YYYY-MM-DD -> DD/MM/YYYY (FR). */
  function formatDate(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    return m ? m[3] + '/' + m[2] + '/' + m[1] : iso;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
})();
